"use client";
import {
  Checkbox,
  CheckboxOnChangeData,
  Input,
  Label,
  makeStyles,
  tokens,
  useId,
} from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ButtonWithLoading } from "../components/ButtonWithLoading";
import {
  useUpdateUtilityLinks,
  useUtilityLinksGet,
} from "../hooks/useUtilityLinks";
import {
  UtilityLinks,
  UtilityLinksTopBarVisibility,
} from "../models/UtilityLinks";

const useClasses = makeStyles({
  root: {
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    padding: tokens.spacingVerticalM,
    gap: tokens.spacingVerticalM,
  },
  buttons: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  utilityLinkSettings: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "1000px",
  },
});

type UtilityLinkKey = keyof UtilityLinksTopBarVisibility;

const createDefaultTopBarVisibility = (): UtilityLinksTopBarVisibility => ({
  emergencyInformation: false,
  improve: true,
  dictionary: false,
  support: false,
  chat: false,
});

const normalizeUtilityLinks = (links: UtilityLinks): UtilityLinks => ({
  ...links,
  topBarVisibility: {
    ...createDefaultTopBarVisibility(),
    ...links.topBarVisibility,
  },
});

function UtilityLinksAdminPage() {
  const classes = useClasses();
  const { t } = useTranslation();
  const { data: utilityLinks, isLoading: utilityLinksLoading } =
    useUtilityLinksGet();

  const { mutateAsync: saveUtilityLinks, isPending: utilityLinkSaving } =
    useUpdateUtilityLinks();

  const [newUtilitySettings, setNewUtilitySettings] = useState<
    UtilityLinks | undefined
  >();

  const updateEmergencyInfo = (newLink: string) => {
    const newSettings = getUtilityLinksClone();

    newSettings.emergencyInformation = newLink;
    setNewUtilitySettings(newSettings);
  };

  const updateImprove = (newLink: string) => {
    const newSettings = getUtilityLinksClone();

    newSettings.improve = newLink;
    setNewUtilitySettings(newSettings);
  };

  const updateDictionary = (newLink: string) => {
    const newSettings = getUtilityLinksClone();

    newSettings.dictionary = newLink;
    setNewUtilitySettings(newSettings);
  };

  const updateSupport = (newLink: string) => {
    const newSettings = getUtilityLinksClone();

    newSettings.support = newLink;
    setNewUtilitySettings(newSettings);
  };

  const updateChat = (newLink: string) => {
    const newSettings = getUtilityLinksClone();

    newSettings.chat = newLink;
    setNewUtilitySettings(newSettings);
  };

  const updateTopBarVisibility = (
    utilityLinkKey: UtilityLinkKey,
    data: CheckboxOnChangeData
  ) => {
    const newSettings = getUtilityLinksClone();

    newSettings.topBarVisibility = {
      ...createDefaultTopBarVisibility(),
      ...newSettings.topBarVisibility,
      [utilityLinkKey]: data.checked === true,
    };

    setNewUtilitySettings(newSettings);
  };

  const handleUtilityLinkSave = async () => {
    if (!newUtilitySettings) {
      return;
    }

    await saveUtilityLinks(newUtilitySettings);
  };

  const getUtilityLinksClone = () => {
    return JSON.parse(JSON.stringify(newUtilitySettings)) as UtilityLinks;
  };

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect */
    setNewUtilitySettings(
      utilityLinks ? normalizeUtilityLinks(utilityLinks) : undefined
    );
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [utilityLinks, setNewUtilitySettings]);

  const emergencyInformationId = useId("emergencyInformation");
  const improveId = useId("improve");
  const dictionaryId = useId("dictionary");
  const supportId = useId("support");
  const chatId = useId("chat");

  return (
    <div className={classes.root}>
      <div className={classes.utilityLinkSettings}>
        <h3>{t("utilityLinkSettingsHeader")}</h3>
        <p>{t("utilityLinksDescription")}</p>
        {!utilityLinksLoading && utilityLinks && newUtilitySettings && (
          <>
            <Label weight="semibold" htmlFor={emergencyInformationId}>
              {t("utilityEmergencyInfoLabel")}
            </Label>
            <Input
              id={emergencyInformationId}
              value={newUtilitySettings.emergencyInformation}
              onChange={(_ev, data) => updateEmergencyInfo(data.value)}
            />
            <Checkbox
              label={t("utilityTopBarVisibilityLabel")}
              checked={
                newUtilitySettings.topBarVisibility?.emergencyInformation ??
                false
              }
              onChange={(_ev, data) =>
                updateTopBarVisibility("emergencyInformation", data)
              }
            />
            <Label weight="semibold" htmlFor={improveId}>
              {t("utilityImproveLabel")}
            </Label>
            <Input
              id={improveId}
              value={newUtilitySettings.improve}
              onChange={(_ev, data) => updateImprove(data.value)}
            />
            <Checkbox
              label={t("utilityTopBarVisibilityLabel")}
              checked={newUtilitySettings.topBarVisibility?.improve ?? true}
              onChange={(_ev, data) => updateTopBarVisibility("improve", data)}
            />
            <Label weight="semibold" htmlFor={dictionaryId}>
              {t("utilityDictionaryLabel")}
            </Label>
            <Input
              id={dictionaryId}
              value={newUtilitySettings.dictionary}
              onChange={(_ev, data) => updateDictionary(data.value)}
            />
            <Checkbox
              label={t("utilityTopBarVisibilityLabel")}
              checked={newUtilitySettings.topBarVisibility?.dictionary ?? false}
              onChange={(_ev, data) =>
                updateTopBarVisibility("dictionary", data)
              }
            />
            <Label weight="semibold" htmlFor={supportId}>
              {t("utilitySupportLabel")}
            </Label>
            <Input
              id={supportId}
              value={newUtilitySettings.support}
              onChange={(_ev, data) => updateSupport(data.value)}
            />
            <Checkbox
              label={t("utilityTopBarVisibilityLabel")}
              checked={newUtilitySettings.topBarVisibility?.support ?? false}
              onChange={(_ev, data) => updateTopBarVisibility("support", data)}
            />
            <Label weight="semibold" htmlFor={chatId}>
              {t("utilityChatLabel")}
            </Label>
            <Input
              id={chatId}
              value={newUtilitySettings.chat}
              onChange={(_ev, data) => updateChat(data.value)}
            />
            <Checkbox
              label={t("utilityTopBarVisibilityLabel")}
              checked={newUtilitySettings.topBarVisibility?.chat ?? false}
              onChange={(_ev, data) => updateTopBarVisibility("chat", data)}
            />
          </>
        )}
      </div>
      <div>
        <ButtonWithLoading
          text={t("saveUtilityLinks")}
          isLoading={utilityLinkSaving}
          onClick={handleUtilityLinkSave}
        />
      </div>
    </div>
  );
}

export default UtilityLinksAdminPage;
